import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Zap, Timer, Trophy, RotateCcw, Play, Pause } from 'lucide-react';

const DinoGame = () => {
  const { t } = useTranslation();
  const [isPlaying, setIsPlaying] = useState(false);
  const [score, setScore] = useState(0);
  const [highScore, setHighScore] = useState(0);
  const [time, setTime] = useState(0);
  const [gameOver, setGameOver] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  
  const canvasRef = useRef(null);
  const gameLoopRef = useRef(null);
  const animationRef = useRef(null);
  
  // Game state
  const [dinoY, setDinoY] = useState(0);
  const [dinoVelocity, setDinoVelocity] = useState(0);
  const [obstacles, setObstacles] = useState([]);
  const [groundY] = useState(300);
  const [gameSpeed, setGameSpeed] = useState(5);
  
  const gravity = 0.8;
  const jumpForce = -15;
  const dinoHeight = 40;
  const dinoWidth = 40;

  const startGame = () => {
    setIsPlaying(true);
    setGameOver(false);
    setScore(0);
    setTime(0);
    setDinoY(0);
    setDinoVelocity(0);
    setObstacles([]);
    setGameSpeed(5);
    setIsPaused(false);
  };

  const pauseGame = () => {
    setIsPaused(!isPaused);
  };

  const resetGame = () => {
    setIsPlaying(false);
    setGameOver(false);
    setScore(0);
    setTime(0);
    setDinoY(0);
    setDinoVelocity(0);
    setObstacles([]);
    setGameSpeed(5);
    setIsPaused(false);
  };

  const jump = () => {
    if (dinoY === 0 && !gameOver && !isPaused) {
      setDinoVelocity(jumpForce);
    }
  };

  const updateGame = useCallback(() => {
    if (!isPlaying || gameOver || isPaused) return;

    // Update dino physics
    setDinoY(prevY => {
      const newY = prevY + dinoVelocity;
      if (newY <= 0) {
        setDinoVelocity(0);
        return 0;
      }
      return newY;
    });
    setDinoVelocity(prev => prev + gravity);

    // Update obstacles
    setObstacles(prev => {
      const newObstacles = prev
        .map(obs => ({ ...obs, x: obs.x - gameSpeed }))
        .filter(obs => obs.x > -50);
      
      // Add new obstacles
      if (Math.random() < 0.02) {
        newObstacles.push({
          x: 800,
          y: groundY,
          width: 30,
          height: 50,
          type: Math.random() < 0.3 ? 'bird' : 'cactus'
        });
      }
      
      return newObstacles;
    });

    // Check collisions
    const dinoRect = {
      x: 100,
      y: groundY - dinoHeight - dinoY,
      width: dinoWidth,
      height: dinoHeight
    };

    const collision = obstacles.some(obs => {
      return !(dinoRect.x + dinoRect.width < obs.x || 
               dinoRect.x > obs.x + obs.width ||
               dinoRect.y + dinoRect.height < obs.y ||
               dinoRect.y > obs.y + obs.height);
    });

    if (collision) {
      setGameOver(true);
      setIsPlaying(false);
      if (score > highScore) {
        setHighScore(score);
      }
      return;
    }

    // Update score and speed
    setScore(prev => prev + 1);
    setGameSpeed(prev => prev + 0.01);
  }, [isPlaying, gameOver, isPaused, dinoVelocity, obstacles, gameSpeed, score, highScore]);

  useEffect(() => {
    if (isPlaying && !gameOver && !isPaused) {
      gameLoopRef.current = setInterval(updateGame, 16); // ~60 FPS
    }
    return () => {
      if (gameLoopRef.current) {
        clearInterval(gameLoopRef.current);
      }
    };
  }, [isPlaying, gameOver, isPaused, updateGame]);

  useEffect(() => {
    let interval;
    if (isPlaying && !gameOver && !isPaused) {
      interval = setInterval(() => {
        setTime(prev => prev + 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [isPlaying, gameOver, isPaused]);

  useEffect(() => {
    const handleKeyPress = (e) => {
      if (e.code === 'Space') {
        e.preventDefault();
        if (!isPlaying) {
          startGame();
        } else {
          jump();
        }
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [isPlaying]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className="max-w-4xl mx-auto">
      {/* Game Header */}
      <div className="cyber-card p-6 mb-8">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
          <div className="flex items-center justify-center space-x-2">
            <Zap className="h-6 w-6 text-neon-blue" />
            <div>
              <p className="text-sm text-gray-400 font-tech">{t('game.score')}</p>
              <p className="text-2xl font-cyber font-bold text-neon-blue">{score}</p>
            </div>
          </div>
          
          <div className="flex items-center justify-center space-x-2">
            <Trophy className="h-6 w-6 text-neon-green" />
            <div>
              <p className="text-sm text-gray-400 font-tech">High Score</p>
              <p className="text-2xl font-cyber font-bold text-neon-green">{highScore}</p>
            </div>
          </div>
          
          <div className="flex items-center justify-center space-x-2">
            <Timer className="h-6 w-6 text-neon-pink" />
            <div>
              <p className="text-sm text-gray-400 font-tech">{t('game.time')}</p>
              <p className="text-2xl font-cyber font-bold text-neon-pink">{formatTime(time)}</p>
            </div>
          </div>
          
          <div className="flex items-center justify-center space-x-2">
            <RotateCcw className="h-6 w-6 text-neon-yellow" />
            <button
              onClick={resetGame}
              className="cyber-button text-sm px-4 py-2"
            >
              Reset
            </button>
          </div>
        </div>
      </div>

      {/* Game Instructions */}
      {!isPlaying && !gameOver && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="cyber-card p-6 mb-8 text-center"
        >
          <h3 className="text-2xl font-cyber font-bold text-white mb-4">
            Chrome Dino Runner
          </h3>
          <p className="text-gray-300 font-tech mb-6">
            Press SPACE to jump and avoid obstacles! Survive as long as possible and beat your high score.
          </p>
          <button
            onClick={startGame}
            className="cyber-button text-lg px-8 py-3"
          >
            Start Game
          </button>
        </motion.div>
      )}

      {/* Game Over Message */}
      {gameOver && (
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          className="cyber-card p-6 mb-8 text-center border-neon-red/50"
        >
          <h3 className="text-3xl font-cyber font-bold text-neon-red mb-4">
            Game Over!
          </h3>
          <p className="text-gray-300 font-tech mb-4">
            Score: {score} | Time: {formatTime(time)}
          </p>
          <div className="flex justify-center space-x-4">
            <button
              onClick={startGame}
              className="cyber-button text-lg px-6 py-3"
            >
              Play Again
            </button>
            <button
              onClick={resetGame}
              className="cyber-button text-lg px-6 py-3 border-neon-red text-neon-red hover:bg-neon-red hover:text-dark-300"
            >
              Reset
            </button>
          </div>
        </motion.div>
      )}

      {/* Game Canvas */}
      <div className="cyber-card p-6">
        <div className="relative">
          {/* Pause Button */}
          {isPlaying && !gameOver && (
            <button
              onClick={pauseGame}
              className="absolute top-4 right-4 z-10 cyber-button text-sm px-3 py-2"
            >
              {isPaused ? <Play className="h-4 w-4" /> : <Pause className="h-4 w-4" />}
            </button>
          )}
          
          {/* Game Area */}
          <div 
            className="relative w-full h-80 bg-dark-100 border-2 border-neon-blue/30 rounded-lg overflow-hidden"
            style={{ background: 'linear-gradient(to bottom, #1a1a2e 0%, #16213e 100%)' }}
          >
            {/* Ground */}
            <div className="absolute bottom-0 w-full h-2 bg-neon-green"></div>
            
            {/* Dino */}
            <motion.div
              className="absolute bottom-2 w-10 h-10 bg-neon-blue rounded"
              style={{
                left: '100px',
                bottom: `${groundY - dinoHeight - dinoY}px`
              }}
              animate={{
                y: dinoY > 0 ? [0, -5, 0] : 0
              }}
              transition={{
                duration: 0.5,
                repeat: dinoY > 0 ? Infinity : 0
              }}
            />
            
            {/* Obstacles */}
            {obstacles.map((obs, index) => (
              <div
                key={index}
                className={`absolute w-8 h-12 ${
                  obs.type === 'bird' ? 'bg-neon-pink' : 'bg-neon-red'
                } rounded`}
                style={{
                  left: `${obs.x}px`,
                  bottom: `${obs.y}px`
                }}
              />
            ))}
            
            {/* Score Display */}
            <div className="absolute top-4 left-4 text-neon-blue font-cyber font-bold text-2xl">
              {score}
            </div>
          </div>
        </div>
      </div>

      {/* Controls Info */}
      <div className="cyber-card p-6 mt-8">
        <div className="text-center">
          <h4 className="text-xl font-cyber font-bold text-white mb-4">
            Controls
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-gray-300 font-tech">
            <div>
              <p className="font-bold text-neon-blue">SPACE</p>
              <p>Jump / Start Game</p>
            </div>
            <div>
              <p className="font-bold text-neon-green">Pause</p>
              <p>Pause/Resume Game</p>
            </div>
            <div>
              <p className="font-bold text-neon-pink">Reset</p>
              <p>Reset to Menu</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DinoGame;
